import React, { useState, useEffect } from 'react';
import { MessageSquare, Plus, Trash2, PanelLeftClose, PanelLeft, Pencil } from 'lucide-react';
import FormattedMessageContent from './FormattedMessageContent';

const ChatTab = ({
  chatMessages,
  chatInput,
  setChatInput,
  isTyping,
  handleChatSubmit,
  messagesScrollRef,
  chatEndRef,
  chatThreads,
  activeChatThreadId,
  onNewChat,
  onSelectThread,
  onArchiveThread,
  onRenameThread,
  projectLabel
}) => {
  const [listOpen, setListOpen] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');

  useEffect(() => {
    if (!editingId) return;
    const t = chatThreads?.find((x) => x.id === editingId);
    if (t) setEditValue(t.title || '');
  }, [editingId, chatThreads]);

  const startEdit = (e, thread) => {
    e.stopPropagation();
    setEditingId(thread.id);
    setEditValue(thread.title || '');
  };

  const commitRename = async (threadId) => {
    const v = editValue.trim();
    if (v && onRenameThread) {
      await onRenameThread(threadId, v);
    }
    setEditingId(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
  };

  return (
    <div className="space-y-4">
      <div className="text-xs text-gray-500">
        Chats for <span className="font-medium text-gray-700">{projectLabel}</span> (saved; double-click a name to rename)
      </div>
      <div className="bg-white/80 backdrop-blur-xl rounded-2xl border border-gray-200/50 shadow-sm flex h-[min(70vh,640px)] min-h-0">
        {listOpen ? (
          <aside className="w-56 shrink-0 border-r border-gray-200/60 flex flex-col min-h-0 bg-slate-50/80 rounded-l-2xl transition-all duration-200">
            <div className="p-3 border-b border-gray-200/60 flex items-center justify-between gap-2">
              <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Chats</span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={onNewChat}
                  className="p-1.5 rounded-lg bg-white border border-gray-200 hover:bg-slate-100 text-gray-700"
                  title="New chat"
                >
                  <Plus className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setListOpen(false)}
                  className="p-1.5 rounded-lg text-gray-500 hover:bg-slate-100"
                  title="Hide list"
                >
                  <PanelLeftClose className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto min-h-0 p-2 space-y-1">
              {(chatThreads || []).map((t) => (
                <div key={t.id} className="group flex items-stretch gap-0.5">
                  {editingId === t.id ? (
                    <div className="flex-1 flex gap-1 items-center min-w-0">
                      <input
                        type="text"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        className="flex-1 min-w-0 text-sm px-2 py-1.5 rounded-lg border border-indigo-300 focus:ring-1 focus:ring-indigo-500"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') commitRename(t.id);
                          if (e.key === 'Escape') cancelEdit();
                        }}
                        onBlur={() => commitRename(t.id)}
                      />
                    </div>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => onSelectThread(t.id)}
                        onDoubleClick={(e) => startEdit(e, t)}
                        className={`flex-1 text-left text-sm px-2 py-2 rounded-lg truncate transition-colors ${
                          t.id === activeChatThreadId
                            ? 'bg-indigo-100 text-indigo-900 font-medium'
                            : 'text-gray-700 hover:bg-white/80'
                        }`}
                      >
                        {t.title || 'New chat'}
                      </button>
                      <button
                        type="button"
                        onClick={(e) => startEdit(e, t)}
                        className="shrink-0 p-1.5 rounded-lg text-gray-400 opacity-0 group-hover:opacity-100 hover:text-indigo-600 hover:bg-indigo-50"
                        title="Rename"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => onArchiveThread(t.id)}
                        className="shrink-0 p-1.5 rounded-lg text-gray-400 opacity-0 group-hover:opacity-100 hover:text-red-600 hover:bg-red-50"
                        title="Remove from list (archive)"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </>
                  )}
                </div>
              ))}
              {(!chatThreads || chatThreads.length === 0) && (
                <div className="text-xs text-gray-500 px-2 py-3">No chats yet. Start a new one.</div>
              )}
            </div>
          </aside>
        ) : (
          <div className="shrink-0 flex flex-col items-center py-2 px-1 border-r border-gray-200/60 bg-slate-50/80 rounded-l-2xl w-10">
            <button
              type="button"
              onClick={() => setListOpen(true)}
              className="p-2 rounded-lg text-gray-600 hover:bg-white border border-gray-200"
              title="Show chat list"
            >
              <PanelLeft className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={onNewChat}
              className="mt-2 p-2 rounded-lg bg-white border border-gray-200 hover:bg-slate-100 text-gray-700"
              title="New chat"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
        )}

        <div className="flex-1 flex flex-col min-w-0 min-h-0">
          <div
            ref={messagesScrollRef}
            className="flex-1 min-h-0 overflow-y-auto p-6 space-y-4"
          >
            {chatMessages.length === 0 ? (
              <div className="text-center py-10">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <MessageSquare className="w-10 h-10 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Knowledge search</h3>
                <p className="text-gray-600 text-sm">
                  Search and query recordings, documents, and tasks. New conversations appear in the list on the left.
                </p>
              </div>
            ) : (
              chatMessages.map((message, index) => (
                <div key={index} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div
                    className={`max-w-[80%] p-4 rounded-2xl ${
                      message.type === 'user' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-900'
                    }`}
                  >
                    {message.type === 'user' ? (
                      <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                    ) : (
                      <FormattedMessageContent content={message.content} inverted={false} />
                    )}
                    <p
                      className={`text-xs mt-2 ${
                        message.type === 'user' ? 'text-blue-100' : 'text-gray-500'
                      }`}
                    >
                      {message.timestamp
                        ? new Date(message.timestamp).toLocaleString()
                        : ''}
                    </p>
                  </div>
                </div>
              ))
            )}
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-gray-100 text-gray-900 p-4 rounded-2xl">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" />
                    <div
                      className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                      style={{ animationDelay: '0.1s' }}
                    />
                    <div
                      className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                      style={{ animationDelay: '0.2s' }}
                    />
                  </div>
                </div>
              </div>
            )}
            <div ref={chatEndRef} className="h-1 shrink-0" />
          </div>
          <div className="p-4 border-t border-gray-200/50 bg-white/60 shrink-0">
            <div className="flex space-x-3">
              <input
                type="text"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleChatSubmit()}
                placeholder="Ask me anything about your brain..."
                className="flex-1 min-w-0 px-4 py-3 bg-gray-50/80 backdrop-blur border border-gray-200/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white/90 transition-all duration-200"
              />
              <button
                type="button"
                onClick={handleChatSubmit}
                disabled={!chatInput.trim() || isTyping}
                className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-medium shadow-lg hover:shadow-xl"
              >
                Send
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatTab;
